import {useCallback, useState} from 'react';
import {runOnJS, useAnimatedReaction, useSharedValue, withTiming} from 'react-native-reanimated';
import {useFocusEffect} from '@react-navigation/native';

import {useHeaderScrollSignal} from '../../context/HeaderCompactContext';
import {useOptionalScrollCollapseContext} from '../../context/ScrollCollapseContext';

import type {Band} from './breakpoints';
import {isSiteHeaderActive} from './headerDenyList';
import {
  compactThresholds,
  type CompactTracker,
  heightsFor,
  INITIAL_TRACKER,
  nextCompactTracker,
  type StoreVariant,
} from './headerGeometry';
import {COMPACT_TRANSITION_MS} from './motion';

/** The tracker plus the thresholds it was last stepped with. Never mutated: each step is new. */
type CompactState = {tracker: CompactTracker; compactAt: number; expandAfterUp: number};

/**
 * One scroll sample. When the thresholds differ from the ones the tracker was stepped with
 * (band change on resize, or store variant change, while scrolled), the tracker restarts from
 * `INITIAL_TRACKER`, so the state is re-evaluated from the current `y`: compact if and only if
 * `y >= compactAt`. Without the restart, the up-step branch of `nextCompactTracker` would carry
 * `prev.compact` across thresholds it was never computed for.
 */
const stepCompactState = (
  prev: CompactState,
  y: number,
  compactAt: number,
  expandAfterUp: number,
): CompactState => {
  'worklet';
  const sameThresholds = prev.compactAt === compactAt && prev.expandAfterUp === expandAfterUp;
  const base = sameThresholds ? prev.tracker : INITIAL_TRACKER;
  return {
    tracker: nextCompactTracker(base, y, {compactAt, expandAfterUp}),
    compactAt,
    expandAfterUp,
  };
};

/**
 * DIS-1840 compact header state for `SiteHeader`. `false` on a static route (no
 * `HeaderCompactContext`), on native, and on web with `HEADER` denied.
 *
 * Writes `ScrollCollapseContext.siteHeaderCompactOffset` (0 full, `full − compact` compact).
 * A compact flip and a threshold change animate it with `withTiming` over
 * `COMPACT_TRANSITION_MS`. Screen focus sets the target directly (ruling R32): the header shows
 * the focused screen's state at once, so an animated focus write would leave the overlays up to
 * `full − compact` away from the header's visible bottom for the transition. Ruling R2: for the `nonEcom` variant
 * `full − compact` is 0, so the offset stays 0; the NonEcomNotice height is not counted.
 *
 * Focus order (Review Focus 3): the focus write uses this screen's own tracker. The reaction
 * runs only while this screen is focused and its `AnimatedHeader` has `ready === 1`, which
 * `AnimatedHeader` sets in a microtask after focus. So when a reset site writes `scrollY` 0 on
 * focus, the reaction re-evaluates from that `scrollY` after the focus write, and its value wins.
 * A blurred screen's reaction is gated off, so it never writes the shared offset.
 */
export const useCompactHeader = (band: Band, variant: StoreVariant): boolean => {
  const active = isSiteHeaderActive();
  const rawSignal = useHeaderScrollSignal();
  const signal = active ? rawSignal : null;
  const collapseCtx = useOptionalScrollCollapseContext();
  const offset = active && collapseCtx ? collapseCtx.siteHeaderCompactOffset : null;

  const {compactAt, expandAfterUp} = compactThresholds(band, variant);
  const {full, compact: compactHeight} = heightsFor(band, variant);
  const maxOffset = full - compactHeight;

  const [compact, setCompact] = useState(false);
  const state = useSharedValue<CompactState>({tracker: INITIAL_TRACKER, compactAt, expandAfterUp});
  const focused = useSharedValue(0);

  useFocusEffect(
    useCallback(() => {
      focused.set(1);
      if (offset) {
        // A static route has no scroll signal: its header is full, so overlays sit at 0.
        const isCompact = signal !== null && state.get().tracker.compact;
        // R32: set, not animate — the overlays must match the header on the first frame.
        offset.set(isCompact ? maxOffset : 0);
      }
      return () => {
        focused.set(0);
      };
    }, [focused, offset, signal, state, maxOffset]),
  );

  useAnimatedReaction(
    () => {
      'worklet';
      if (!signal || focused.get() !== 1 || signal.ready.get() !== 1) return null;
      return signal.scrollY.get();
    },
    y => {
      'worklet';
      if (y === null) return;
      const prev = state.get();
      const next = stepCompactState(prev, y, compactAt, expandAfterUp);
      state.set(next);
      // `nextCompactTracker` returns `prev` on an equal step: compare values, never identity.
      const flipped = next.tracker.compact !== prev.tracker.compact;
      const rethresholded = prev.compactAt !== compactAt || prev.expandAfterUp !== expandAfterUp;
      if (offset && (flipped || rethresholded)) {
        offset.set(
          withTiming(next.tracker.compact ? maxOffset : 0, {duration: COMPACT_TRANSITION_MS}),
        );
      }
      if (flipped) runOnJS(setCompact)(next.tracker.compact);
    },
    [signal, focused, state, offset, compactAt, expandAfterUp, maxOffset],
  );

  return signal ? compact : false;
};
